/* About page (sections/ls-about-*.liquid): year/edition tabs, city map, story + sources drawer.
   Fork of the prototype about.js; all copy comes from the sections' markup. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const $$ = (q, root = document) => Array.from(root.querySelectorAll(q));
  const root = document.querySelector('[data-ab-sources-root]');
  let gsap = null;
  const gsapSrc = root?.dataset.gsapSrc;
  if (gsapSrc && !reduced.matches) {
    const load = () => (window.lsLoadGsap ? window.lsLoadGsap(gsapSrc) : Promise.resolve(window.gsap)).then(g => { gsap = g || window.gsap || null; }).catch(() => {});
    'requestIdleCallback' in window ? requestIdleCallback(load, { timeout: 2500 }) : setTimeout(load, 800);
  }

  const running = new Map();
  function stop(el) {
    const a = running.get(el);
    a?.kill?.(); a?.cancel?.();
    running.delete(el);
    el.style.removeProperty('opacity'); el.style.removeProperty('transform'); el.style.removeProperty('visibility');
  }
  function enter(el, x = 0) {
    stop(el);
    if (reduced.matches) return;
    const done = () => { running.delete(el); el.style.removeProperty('opacity'); el.style.removeProperty('transform'); el.style.removeProperty('visibility'); };
    if (gsap) {
      running.set(el, gsap.fromTo(el, { autoAlpha: .2, x, y: x ? 0 : 8 }, { autoAlpha: 1, x: 0, y: 0, duration: .32, ease: 'power3.out', onComplete: done }));
    } else if (el.animate) {
      const a = el.animate([{ opacity: .3, transform: `translate(${x}px, ${x ? 0 : 8}px)` }, { opacity: 1, transform: 'none' }], { duration: 240, easing: 'cubic-bezier(.23,1,.32,1)' });
      running.set(el, a);
      a.finished.then(done).catch(() => {});
    }
  }

  /* Tabs (journey years, award editions) */
  function tabs(section) {
    const list = section.querySelector('[data-ab-tablist]');
    const wrap = section.querySelector('[data-ab-panels]');
    if (!list || !wrap) return null;
    const buttons = $$('[data-ab-tab]', list);
    const panels = $$('[data-ab-panel]', wrap);
    if (buttons.length < 2 || buttons.length !== panels.length) return null;
    list.hidden = false;
    wrap.classList.remove('is-static');
    panels.forEach(p => { p.tabIndex = 0; });
    const pager = section.querySelector('[data-ab-pager]');
    const prev = section.querySelector('[data-ab-prev]');
    const next = section.querySelector('[data-ab-next]');
    const pos = section.querySelector('[data-ab-position]');
    if (pager) pager.hidden = false;
    let index = 0;
    function select(i, animate = true) {
      if (i < 0 || i >= buttons.length) return;
      index = i;
      buttons.forEach((b, j) => {
        b.setAttribute('aria-selected', String(j === i));
        b.tabIndex = j === i ? 0 : -1;
        stop(panels[j]);
        panels[j].hidden = j !== i;
      });
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === buttons.length - 1;
      if (pos) pos.textContent = (pos.dataset.template || '').replace('[n]', i + 1).replace('[total]', buttons.length);
      const b = buttons[i];
      const left = b.offsetLeft - list.offsetLeft;
      if (left < list.scrollLeft || left + b.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollTo({ left: left - 12, behavior: reduced.matches ? 'instant' : 'smooth' });
      }
      if (animate) enter(panels[i]);
    }
    buttons.forEach((b, i) => b.addEventListener('click', e => select(i, e.detail !== 0)));
    list.addEventListener('keydown', e => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      const cur = buttons.indexOf(document.activeElement);
      if (cur < 0) return;
      e.preventDefault();
      const n = buttons.length;
      const i = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (cur + (e.key === 'ArrowLeft' ? -1 : 1) + n) % n;
      select(i, false);
      buttons[i].focus({ preventScroll: true });
    });
    prev?.addEventListener('click', () => select(index - 1));
    next?.addEventListener('click', () => select(index + 1));
    select(0, false);
    return { select, panels };
  }

  /* Community map */
  function place(pin) {
    const lat = parseFloat(pin.dataset.lat), lon = parseFloat(pin.dataset.lon);
    if (isNaN(lat) || isNaN(lon)) return;
    const A1 = 1.340264, A2 = -0.081106, A3 = 0.000893, A4 = 0.003796, M = Math.sqrt(3) / 2;
    const p = Math.asin(M * Math.sin(lat * Math.PI / 180)), p2 = p * p, p6 = p2 * p2 * p2;
    const x = (lon * Math.PI / 180) * Math.cos(p) / (M * (A1 + 3 * A2 * p2 + p6 * (7 * A3 + 9 * A4 * p2)));
    const y = p * (A1 + A2 * p2 + p6 * (A3 + A4 * p2));
    pin.style.left = (18.48467 * x + 49.99901).toFixed(3) + '%';
    pin.style.top = (-42.62431 * y + 56.21881).toFixed(3) + '%';
    pin.removeAttribute('data-unplaced');
  }
  function community(section) {
    const pins = $$('[data-ab-pin]', section);
    pins.forEach(place);
    const list = section.querySelector('[data-ab-citylist]');
    const wrap = section.querySelector('[data-ab-panels]');
    const buttons = list ? $$('[data-ab-city]', list) : [];
    const panels = wrap ? $$('[data-ab-panel]', wrap) : [];
    if (buttons.length < 2 || buttons.length !== panels.length) return null;
    list.hidden = false;
    wrap.classList.remove('is-static');
    function select(i, animate = true) {
      buttons.forEach((b, j) => b.setAttribute('aria-pressed', String(i === j)));
      pins.forEach((p, j) => p.classList.toggle('is-active', i === j));
      panels.forEach((p, j) => { stop(p); p.hidden = i !== j; });
      if (animate) enter(panels[i]);
    }
    buttons.forEach((b, i) => b.addEventListener('click', e => select(i, e.detail !== 0)));
    select(0, false);
    return { select, panels };
  }

  const controllers = [];
  $$('[data-ab-journey], [data-ab-awards]').forEach(s => { const c = tabs(s); if (c) controllers.push(c); });
  $$('[data-ab-community]').forEach(s => { const c = community(s); if (c) controllers.push(c); });

  /* Drawer */
  const drawer = root?.querySelector('[data-ab-drawer]');
  if (drawer && typeof drawer.showModal === 'function') {
    const body = drawer.querySelector('[data-ab-drawer-body]');
    const label = drawer.querySelector('[data-ab-drawer-label]');
    let opener = null, oldOverflow = '';
    const sourceRecord = id => root.querySelector(`template[data-ab-source="${CSS.escape(id)}"]`)?.content.cloneNode(true) || null;

    function open(tpl, trigger, fill) {
      opener = trigger;
      body.replaceChildren(tpl.content.cloneNode(true));
      fill?.(body);
      const title = body.querySelector('[data-ab-drawer-title]');
      if (title) title.id = 'ab-drawer-title';
      label.textContent = tpl.dataset.label || '';
      body.scrollTop = 0;
      window.dispatchEvent(new CustomEvent('linsoul:overlay-opening', { detail: { kind: 'about-story' } }));
      drawer.showModal();
      oldOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      enter(drawer, 28);
      drawer.querySelector('[data-ab-close]').focus({ preventScroll: true });
    }
    function close(restore = true) {
      if (!drawer.open) return;
      stop(drawer);
      drawer.close();
      document.documentElement.style.overflow = oldOverflow;
      if (restore && opener?.isConnected) opener.focus({ preventScroll: true });
    }
    function openStory(id, trigger) {
      const tpl = document.querySelector(`template[data-ab-story-tpl="${CSS.escape(id)}"]`);
      if (!tpl) return false;
      open(tpl, trigger, b => {
        const box = b.querySelector('[data-ab-source-ids]');
        if (!box) return;
        let n = 0;
        box.dataset.abSourceIds.split(',').map(x => x.trim()).filter(Boolean).forEach(sid => {
          const rec = sourceRecord(sid);
          if (rec) { box.append(rec); n++; }
        });
        if (!n) box.remove();
      });
      return true;
    }
    function openSources(trigger) {
      const tpl = root.querySelector('template[data-ab-sources-tpl]');
      if (!tpl) return false;
      open(tpl, trigger, b => {
        const list = b.querySelector('[data-ab-source-list]');
        $$('template[data-ab-source]', root).forEach(t => list.append(t.content.cloneNode(true)));
      });
      return true;
    }

    document.addEventListener('click', e => {
      const story = e.target.closest('[data-ab-story]');
      const src = e.target.closest('[data-ab-sources]');
      if (story && openStory(story.dataset.abStory, story)) e.preventDefault();
      else if (src && openSources(src)) e.preventDefault();
    });
    drawer.querySelector('[data-ab-close]').addEventListener('click', () => close());
    drawer.addEventListener('cancel', e => { e.preventDefault(); close(); });
    drawer.addEventListener('click', e => {
      if (e.target !== drawer) return;
      const r = drawer.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
    });
    window.addEventListener('linsoul:overlay-opening', e => { if (e.detail?.kind !== 'about-story' && drawer.open) close(false); });
  }

  /* Smooth in-page anchors (header offset comes from scroll-margin-top) */
  document.addEventListener('click', e => {
    const a = e.target.closest('.ab a[href^="#"]');
    if (!a || a.matches('[data-ab-story], [data-ab-sources]') || a.hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(a.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
    history.replaceState(null, '', location.pathname + location.search + a.hash);
  });

  /* Theme editor: selecting a moment / edition / city shows it */
  document.addEventListener('shopify:block:select', e => {
    controllers.forEach(c => {
      const i = c.panels.findIndex(p => p.contains(e.target) || p === e.target);
      if (i >= 0) c.select(i, false);
    });
  });

  reduced.addEventListener?.('change', () => { for (const el of running.keys()) stop(el); });
})();
